/* istanbul ignore file */
/* It's not possible to fill VGS fields in Cypress */
import { Alert, Button } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import { useFormikContext } from 'formik'
import React, { ReactElement, useCallback, useState } from 'react'

import { Response } from '@api/reservation/checkCard'
import Loader from '@components/Loader'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import vgs, { VGS } from '@lib/vgs'
import { VgsRequiredFieldError, VgsInvalidCardError, createVgsError } from '@lib/vgs/errors'
import { CheckoutFormData, VGSCardData } from '@pages/Checkout/hooks/useInitialFormValues'
import BillingAddress from '@pages/Checkout/Payment/BillingAddress'
import { CustomFields } from '@pages/Checkout/Payment/CheckPayment/CustomFields'
import { useCheckPayment } from '@pages/Checkout/Payment/CheckPayment/useCheckPayment'
import CVCField from '@pages/Checkout/Payment/CreditCardForm/VGS/CVCField'
import ExpDateField from '@pages/Checkout/Payment/CreditCardForm/VGS/ExpDateField'
import HolderField from '@pages/Checkout/Payment/CreditCardForm/VGS/HolderField'
import NumberField from '@pages/Checkout/Payment/CreditCardForm/VGS/NumberField'
import { SplitExpiryField } from '@pages/Checkout/Payment/CreditCardForm/VGS/SplitExpiryField'
import { withPluginSlot } from '@plugins/index'
import { useSettings } from '@queries/settings'

import '@pages/Checkout/Payment/CreditCardForm/VGS/index.scss'

export const monthsRegex = '/^(01|02|03|04|05|06|07|08|09|10|11|12)$/'
export const yearRegex = '/^[0-9]{4}$/'

interface VGSCardProps {
  vgsForm?: VGS.Form | null
  onCardCheck: (token: string) => void
}

const getCardError = (error: unknown, t: (key: string) => string): string => {
  if (typeof error === 'object' && error !== null) {
    const vgsError = createVgsError(error)

    if (vgsError instanceof VgsRequiredFieldError) {
      return t('checkCard.fillRequiredFields')
    } else if (vgsError instanceof VgsInvalidCardError) {
      return t('checkCard.cardInvalidError')
    } else {
      return t('checkCard.error')
    }
  } else {
    return `${error}`
  }
}

const VGSCard = ({ vgsForm, onCardCheck }: VGSCardProps): ReactElement => {
  const { t } = useTranslation()
  const [{ paymentProvidersMethods, billingAddress }] = useSettings()
  const {
    setFieldValue,
    values,
    touched: { paymentMethodData: formTouched },
  } = useFormikContext<CheckoutFormData>()

  const useCVC = paymentProvidersMethods.creditCard?.options?.cvc

  const handleResponse = useCallback(
    (response: Response) => {
      const formikFields: Record<string, string> = {}

      response.customFields.forEach(field => {
        formikFields[field.name] = field.value
      })

      setFieldValue('creditCardCustomFields', formikFields)
    },
    [setFieldValue],
  )

  const {
    handleCheck,
    checkResult,
    isEnabled: isCardCheckEnabled,
    error,
    cardError,
    setCardError,
  } = useCheckPayment(handleResponse)

  const cardExpirySplit = paymentProvidersMethods.creditCard?.options?.cardExpirySplit || false
  const storage = paymentProvidersMethods.creditCard?.options?.tokenizationStorage ?? 'persistent'

  const [isValidating, setValidating] = useState(false)

  const handleClick = useCallback(async () => {
    if (vgsForm) {
      try {
        setValidating(true)
        const { result: cardData } = await vgs.promisedTokenize<VGSCardData>(vgsForm)
        cardData.cardType = vgsForm.state.cardNumber.cardType
        onCardCheck(cardData.cardNumber)
        handleCheck('credit_card', cardData)
      } catch (error) {
        const errorMessage = getCardError(error, t)
        setCardError(errorMessage)
      } finally {
        setValidating(false)
      }
    }
  }, [handleCheck, onCardCheck, setCardError, vgsForm])

  /* istanbul ignore if */
  if (!vgsForm) return <Loader loading />

  return (
    <>
      {isCardCheckEnabled && (error || cardError) && (
        <div className="row gap-3" data-tag="check-card-error">
          <div className="cell-10">
            <Alert message={cardError || t('checkCard.cardInvalidError')} type="error" variant="compact" />
          </div>
        </div>
      )}
      <div className="column row-lg gap-3">
        <div className="cell-7 cell-sm-12">
          <div className="column gap-3">
            <div
              className={cn(
                'cell-12',
                bem('vgs', {
                  'split-fields': cardExpirySplit,
                  'single-field': !cardExpirySplit,
                }),
              )}
            >
              <NumberField
                form={vgsForm}
                name="cardNumber"
                placeholder={t('checkout.card.number')}
                touched={formTouched}
                storage={storage}
              />
              {!cardExpirySplit && (
                <ExpDateField
                  form={vgsForm}
                  name="cardExpiry"
                  placeholder={t('checkout.card.expireAt')}
                  touched={formTouched}
                  storage={storage}
                />
              )}
              {cardExpirySplit && [
                <SplitExpiryField
                  validation={monthsRegex}
                  key="card-expiration-month"
                  id="card-expiration-month"
                  form={vgsForm}
                  name="expiryMonth"
                  placeholder={t('checkout.card.expiryMonth')}
                  touched={formTouched}
                  storage={storage}
                />,
                <SplitExpiryField
                  validation={yearRegex}
                  key="card-expiration-year"
                  id="card-expiration-year"
                  form={vgsForm}
                  name="expiryYear"
                  placeholder={t('checkout.card.expiryYear')}
                  touched={formTouched}
                  storage={storage}
                />,
              ]}
              {useCVC && (
                <CVCField
                  form={vgsForm}
                  name="cvv"
                  placeholder={t('checkout.card.cvc')}
                  touched={formTouched}
                  storage="volatile"
                />
              )}
              <HolderField
                form={vgsForm}
                name={'cardHolderName'}
                placeholder={t('checkout.card.holder')}
                touched={formTouched}
                storage={storage}
              />
            </div>
            {billingAddress.enabled && (
              <div className="cell-12">
                <BillingAddress />
              </div>
            )}
          </div>
        </div>
        {isCardCheckEnabled && (
          <div className="row cell-5 cell-sm-12">
            <Button
              loading={isValidating || values.isPaymentDetailsLoading}
              data-tag="check-card-button"
              color="secondary"
              className="check-button"
              onClick={handleClick}
            >
              {t('checkCard.button')}
            </Button>
          </div>
        )}
      </div>
      {checkResult?.customFields && checkResult.customFields.length > 0 && (
        <div className="row gap-3">
          <div className="cell-12">
            <CustomFields fields={checkResult.customFields} />
          </div>
        </div>
      )}
    </>
  )
}

export default withPluginSlot(VGSCard, 'payment.form.vgs')
